import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import api, { fileUrl, formatApiErrorDetail } from "../lib/api";
import { usePrimaryPropertyId } from "../hooks/useMyProperties";
import { Loader, SectionHeading } from "../components/Loader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { UploadButton } from "../components/UploadButton";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

const CATEGORIES = ["Plumbing", "Electrical", "HVAC", "Roofing", "Flooring", "Appliances", "Windows & Doors", "Drywall & Paint", "Cabinetry", "Exterior", "Other"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];

export default function ServiceNew() {
  const { propertyId, isLoading } = usePrimaryPropertyId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({ category: "", location: "", description: "", date_discovered: "", priority: "Normal", preferred_availability: "", permission_to_enter: false });
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.category || !form.description) { setError("Please provide a category and description."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/service-requests", { ...form, property_id: propertyId, attachments });
      qc.invalidateQueries({ queryKey: ["service-requests"] });
      toast.success(`Service Request #${data.number} submitted`);
      navigate(`/service/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-2xl arq-fade-up">
      <SectionHeading overline="Service" title="Submit a Service Request" />
      <form onSubmit={submit} className="bg-arq-card border border-border rounded-md p-6 space-y-5" data-testid="service-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Issue Category *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1.5" data-testid="sr-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Location in Home</Label>
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Master Bathroom" className="mt-1.5" data-testid="sr-location" />
          </div>
        </div>
        <div>
          <Label>Description *</Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder="Describe the issue in detail…" className="mt-1.5" data-testid="sr-description" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Date Discovered</Label>
            <Input type="date" value={form.date_discovered} onChange={(e) => set("date_discovered", e.target.value)} className="mt-1.5" data-testid="sr-date" />
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className="mt-1.5" data-testid="sr-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Preferred Availability</Label>
          <Input value={form.preferred_availability} onChange={(e) => set("preferred_availability", e.target.value)} placeholder="e.g. Weekday mornings" className="mt-1.5" data-testid="sr-availability" />
        </div>
        <div>
          <Label>Photos / Videos</Label>
          <div className="mt-1.5 flex items-center gap-3 flex-wrap">
            <UploadButton multiple accept="image/*,video/*" label="Attach media" onUploaded={(r) => setAttachments((a) => [...a, r])} testid="sr-upload" />
            {attachments.map((a, i) => (
              <div key={i} className="relative h-16 w-16 rounded-md overflow-hidden border border-border bg-arq-surface">
                {a.content_type?.startsWith("image") ? <img src={fileUrl(a.url)} alt="" className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full text-[9px] p-1 text-center">{a.filename}</div>}
                <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X className="h-3 w-3 text-white" /></button>
              </div>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={form.permission_to_enter} onCheckedChange={(v) => set("permission_to_enter", !!v)} data-testid="sr-permission" />
          <span className="text-sm text-arq-ink">I grant permission to enter the property if I'm not home.</span>
        </label>
        {error && <p className="text-sm text-destructive" data-testid="sr-error">{error}</p>}
        <div className="flex gap-3">
          <Button type="submit" disabled={busy} data-testid="sr-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Submit Request</Button>
          <Button type="button" variant="outline" onClick={() => navigate("/service")}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
